import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { MessageCircle } from 'lucide-react';
import type { NostrEvent } from '@nostrify/nostrify';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { CommentItem } from '@/components/CommentItem';
import { LoginArea } from '@/components/auth/LoginArea';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useOutboxPublish } from '@/hooks/useOutboxPublish';
import { useToast } from '@/hooks/useToast';

interface CommentSectionProps {
  article: NostrEvent;
  coordinate: string;
  comments: NostrEvent[];
  isLoading?: boolean;
}

export function CommentSection({
  article,
  coordinate,
  comments,
  isLoading,
}: CommentSectionProps) {
  const { user, metadata } = useCurrentUser();
  const { mutateAsync: publish, isPending } = useOutboxPublish();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [text, setText] = useState('');

  // Only top-level comments: those whose parent (lowercase `a`) is the article.
  const topLevel = comments.filter((c) => {
    const parentA = c.tags.find(([n]) => n === 'a')?.[1];
    return parentA === coordinate;
  });
  const shown = topLevel.length > 0 ? topLevel : comments;

  const handleSubmit = async () => {
    const content = text.trim();
    if (!content || !user) return;

    try {
      // NIP-22: root (uppercase) and parent (lowercase) both point at the
      // article for a top-level comment.
      await publish({
        kind: 1111,
        content,
        tags: [
          ['A', coordinate],
          ['K', String(article.kind)],
          ['P', article.pubkey],
          ['a', coordinate],
          ['k', String(article.kind)],
          ['p', article.pubkey],
        ],
      });
      setText('');
      toast({ title: 'Comment posted' });
      // Give relays a moment, then refresh.
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: ['article-interactions', coordinate],
        });
      }, 1200);
    } catch {
      toast({ title: 'Failed to post comment', variant: 'destructive' });
    }
  };

  const displayName = metadata?.display_name || metadata?.name || 'You';

  return (
    <section id="comments" className="scroll-mt-24 pt-10 border-t border-border/60">
      <h2 className="flex items-center gap-2 font-serif text-2xl font-bold text-foreground">
        <MessageCircle className="h-5 w-5 text-primary" />
        Comments
        {shown.length > 0 && (
          <span className="text-base font-normal text-muted-foreground">
            ({shown.length})
          </span>
        )}
      </h2>

      {/* Add comment */}
      <div className="mt-6">
        {user ? (
          <div className="flex gap-3">
            <Avatar className="h-9 w-9 shrink-0">
              <AvatarImage src={metadata?.picture} alt={displayName} />
              <AvatarFallback>{displayName.charAt(0).toUpperCase()}</AvatarFallback>
            </Avatar>
            <div className="flex-1 space-y-2">
              <Textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Share your thoughts…"
                rows={3}
                className="resize-none bg-card/60"
              />
              <div className="flex justify-end">
                <Button
                  onClick={handleSubmit}
                  disabled={isPending || !text.trim()}
                  className="rounded-full"
                >
                  {isPending ? 'Posting…' : 'Post comment'}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <Card className="border-dashed bg-card/40">
            <CardContent className="flex flex-col items-center gap-3 py-8 text-center">
              <p className="text-muted-foreground">
                Log in to join the conversation.
              </p>
              <LoginArea className="max-w-60" />
            </CardContent>
          </Card>
        )}
      </div>

      {/* Comment list */}
      <div className="mt-8 space-y-6">
        {isLoading && (
          <p className="text-sm text-muted-foreground">Loading comments…</p>
        )}
        {!isLoading && shown.length === 0 && (
          <p className="text-muted-foreground">
            No comments yet. Be the first to respond.
          </p>
        )}
        {shown
          .slice()
          .sort((a, b) => a.created_at - b.created_at)
          .map((comment) => (
            <CommentItem key={comment.id} comment={comment} />
          ))}
      </div>
    </section>
  );
}
